import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react'
import { authService, UserInfo } from '@/api/auth'

interface AuthContextType {
  user: UserInfo | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => void
  loginWithGoogle: () => void
  installStoreViaTiendaNube: () => void
  logout: () => void
  refreshAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const initializeAuth = useCallback(async () => {
    setIsLoading(true)
    try {
      const token = authService.getAccessToken()
      const userInfo = authService.getUserInfo()

      if (token && userInfo) {
        setUser(userInfo)
        setIsAuthenticated(true)
        authService.initializeAuth()
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (error) {
      console.error('Auth initialization failed:', error)
      setUser(null)
      setIsAuthenticated(false)
      authService.logout()
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  const login = useCallback(() => {
    authService.startGoogleLogin()
  }, [])

  const loginWithGoogle = useCallback(() => {
    authService.startGoogleLogin()
  }, [])

  const installStoreViaTiendaNube = useCallback(() => {
    authService.startUserLoginFlow()
  }, [])

  const logout = useCallback(() => {
    authService.logout()
    setUser(null)
    setIsAuthenticated(false)
    window.location.href = '/'
  }, [])

  const refreshAuth = useCallback(async () => {
    try {
      await initializeAuth()
    } catch (error) {
      console.error('Auth refresh failed:', error)
      logout()
    }
  }, [initializeAuth, logout])

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithGoogle,
    installStoreViaTiendaNube,
    logout,
    refreshAuth
  }), [user, isAuthenticated, isLoading, login, loginWithGoogle, installStoreViaTiendaNube, logout, refreshAuth])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Higher-order component for protecting routes
export function withAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>
) {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth()

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )
    }

    if (!isAuthenticated) {
      // Redirect to login
      window.location.href = '/'
      return null
    }

    return <WrappedComponent {...props} />
  }
}